// src/contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// Create the Auth Context
export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Initialize auth state with data from localStorage if available
  const [authState, setAuthState] = useState(() => {
    try {
      const storedAuth = localStorage.getItem('auth');
      return storedAuth ? JSON.parse(storedAuth) : {
        user: null,
        token: null,
        userType: null, // 'agribusiness', 'farmer', or 'admin'
        isAuthenticated: false
      };
    } catch (error) {
      console.error("Failed to parse auth from localStorage:", error);
      return {
        user: null,
        token: null,
        userType: null,
        isAuthenticated: false
      };
    }
  });

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  // On component mount, check authentication status
  useEffect(() => {
    const loadAuthStatus = async () => {
      if (authState.token) {
        try {
          setAuthState(prev => ({ ...prev, isAuthenticated: true }));
        } catch (error) {
          console.error("Token invalid or expired:", error);
          localStorage.removeItem('auth');
          setAuthState({
            user: null,
            token: null,
            userType: null,
            isAuthenticated: false
          });
        }
      }
      setLoading(false);
    };

    loadAuthStatus();
  }, [authState.token]);

  // Agribusiness login - SIMPLIFIED
  const loginAgribusiness = async (loginData) => {
    setLoading(true);
    try {
      console.log('🔐 Attempting login for:', loginData.email);

      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.lockout) {
          throw new Error(`Account temporarily locked. Please try again in ${data.lockoutTime} minutes.`);
        }
        if (data.requiresVerification) {
          throw new Error('Please verify your email before logging in');
        }
        if (data.error?.includes('Invalid credentials') || data.error?.includes('Invalid email or password')) {
          throw new Error('Invalid email or password');
        }
        if (data.error?.includes('network') || data.error?.includes('Network')) {
          throw new Error('Network error. Please check your connection and try again.');
        }
        throw new Error(data.error || data.message || 'Login failed');
      }

      const newAuthState = {
        user: data.user,
        token: data.token,
        userType: 'agribusiness',
        isAuthenticated: true
      };

      localStorage.setItem('auth', JSON.stringify(newAuthState));
      setAuthState(newAuthState);
      setLoading(false);

      console.log('✅ Login successful for:', data.user.email);

      return data;
    } catch (error) {
      setLoading(false);
      console.error('❌ Login error:', error.message);
      throw error;
    }
  };


// Password reset methods
const requestPasswordReset = async (email) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    // Check if response is HTML (error page)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      const htmlText = await response.text();
      console.error('❌ Server returned HTML instead of JSON:', htmlText.substring(0, 200));
      throw new Error('Server error - please try again later');
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to send reset email');
    }

    return await response.json();
  } catch (error) {
    console.error('Password reset request error:', error);
    throw new Error(error.message || 'Failed to send reset email');
  }
};

const resetPassword = async (token, newPassword) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, newPassword }),
    });

    // Check if response is HTML (error page)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      const htmlText = await response.text();
      console.error('❌ Server returned HTML instead of JSON:', htmlText.substring(0, 200));
      throw new Error('Server error - please try again later');
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to reset password');
    }

    return await response.json();
  } catch (error) {
    console.error('Password reset error:', error);
    throw new Error(error.message || 'Failed to reset password');
  }
};

  // Farmer login - SIMPLIFIED
  const loginFarmer = async (email, password) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/farmers/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Handle both response structures: data.user OR data.farmer
      const userData = data.user || data.farmer || data;

      if (!userData) {
        throw new Error('Invalid response from server: no user data received');
      }

      const newAuthState = {
        user: userData,
        token: data.token,
        userType: 'farmer',
        isAuthenticated: true
      };

      console.log("✅ Farmer login successful for:", userData.email);

      localStorage.setItem('auth', JSON.stringify(newAuthState));
      setAuthState(newAuthState);
      setLoading(false);
      return data;
    } catch (error) {
      setLoading(false);
      localStorage.removeItem('auth');
      setAuthState({
        user: null,
        token: null,
        userType: null,
        isAuthenticated: false
      });
      console.error('Farmer login error:', error);
      throw error;
    }
  };

  // Farmer registration
  const registerFarmer = async (userData) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/farmers/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      setLoading(false);
      return data;
    } catch (error) {
      setLoading(false);
      console.error('Farmer registration error:', error);
      throw error;
    }
  };

  // Agribusiness registration - SIMPLIFIED
  const registerAgribusiness = async (userData) => {
    setLoading(true);

    try {
      console.log('📝 Starting agribusiness registration for:', userData.email);

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();
      console.log('📨 Registration response:', data);

      // ✅ Standardize the return shape so UI logic always works
      if (response.ok) {
        if (data.success) {
          console.log('✅ Registration successful for:', userData.email);
          setLoading(false);

          return {
            success: true,
            requiresVerification: !!data.requiresVerification,
            emailFailed: !!data.emailFailed,
            message:
              data.message ||
              (data.requiresVerification
                ? 'Registration successful! Please check your email to verify your account.'
                : 'Registration successful!'),
          };
        }

        // ⚠️ Handle specific cases where backend didn't mark success=true
        if (data.emailFailed) {
          console.log('⚠️ Registration successful but email failed for:', userData.email);
          setLoading(false);

          return {
            success: true, // still a success — account created
            requiresVerification: true,
            emailFailed: true,
            message:
              data.message ||
              'Account created, but we could not send a verification email. Please try again later.',
          };
        }

        // ❌ Duplicate or known error
        const backendError =
          data.error ||
          data.message ||
          (data.code === 'EMAIL_EXISTS'
            ? 'This email is already registered. Please log in.'
            : 'Registration failed.');

        console.error('❌ Registration failed:', backendError);
        throw new Error(backendError);
      }

      // ❌ Non-OK response (400–500)
      const fallbackError = data.error || data.message || 'Registration request failed.';
      console.error('❌ HTTP error during registration:', fallbackError);
      throw new Error(fallbackError);
    } catch (error) {
      console.error('🔥 Agribusiness registration error:', error.message);

      let friendlyMessage = error.message;

      if (friendlyMessage.toLowerCase().includes('network') || friendlyMessage.toLowerCase().includes('fetch')) {
        friendlyMessage = 'Network error. Please check your connection and try again.';
      } else if (friendlyMessage.toLowerCase().includes('timeout')) {
        friendlyMessage = 'Registration timeout. Please try again.';
      } else if (
        friendlyMessage.toLowerCase().includes('exists') ||
        friendlyMessage.toLowerCase().includes('registered')
      ) {
        friendlyMessage = 'This email is already registered. Please log in instead.';
      }

      setLoading(false);
      throw new Error(friendlyMessage);
    }
  };

  // Set admin auth state (used by admin login component)
  const setAdminAuth = (adminData) => {
    const newAuthState = {
      user: adminData.admin,
      token: adminData.token,
      userType: 'admin',
      isAuthenticated: true
    };

    localStorage.setItem('auth', JSON.stringify(newAuthState));
    setAuthState(newAuthState);
  };

  // Unified logout - SIMPLIFIED
  const logout = () => {
    console.log('🚪 Logging out user...');

    // Store current user type before clearing auth state
    const currentUserType = authState.userType;

    // Clear all auth-related storage
    localStorage.removeItem('auth');
    localStorage.removeItem('failedLoginAttempts');
    localStorage.removeItem('loginLockout');

    setAuthState({
      user: null,
      token: null,
      userType: null,
      isAuthenticated: false
    });

    console.log(`✅ Logout complete, redirecting ${currentUserType}...`);

    // Handle redirect based on user type
    switch (currentUserType) {
      case 'farmer':
        navigate('/');
        break;
      case 'agribusiness':
      case 'admin':
      default:
        navigate('/login');
        break;
    }
  };

  // Helper functions
  const isAgribusiness = () => authState.userType === 'agribusiness';
  const isFarmer = () => authState.userType === 'farmer';
  const isAdmin = () => authState.userType === 'admin';

  const authContextValue = {
    // State
    user: authState.user,
    token: authState.token,
    userType: authState.userType,
    isAuthenticated: authState.isAuthenticated,
    loading,

    // Actions
    loginAgribusiness,
    requestPasswordReset,
    resetPassword,
    loginFarmer,
    registerAgribusiness,
    registerFarmer,
    setAdminAuth,
    logout,

    // Helpers
    isAgribusiness,
    isFarmer,
    isAdmin
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-custom-light-gray flex items-center justify-center font-inter text-custom-teal text-xl">
        <svg className="animate-spin h-8 w-8 mr-3 text-custom-teal" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Authenticating...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};